import { defineStore } from 'pinia';
import { serverUrls } from '../api/constants';
import { callAPI } from '../api/httpservice';
import { useUserStore } from './userStore';
import i18n from '../utils/i18n';
import { useSocketStore } from './socketStore';
import router from '@/router';
import { notify } from '@/utils/toaster';

const scrapCooldownTimers = {};

export const usePeopleStore = defineStore('people', {
  state: () => ({
    people: [],
    listPage: 1,
    listLimit: 10,
    listSearch: '',
    sortByStatus: '',
    selectedExtractionValue: null,
    sortByVerify: null,
    totalPeople: 0,
    selectedPeople: null,
    peoplePhotos: [],
    stats: null,
    loadingPhotos: false,
    sortBy: 'createdAt',
    sortOrder: 'DESC',
    scrapCooldownIds: [],
  }),
  actions: {
    setupListeners() {
      const socketStore = useSocketStore();
      const socket = socketStore.socket;

      if (!socket) {
        console.error('Socket not connected');
        return;
      }

      /**
       * People Events
       */
      socket.on('person.created', (data) => {
        // check if person already exists in the list, or if it matches the current filters
        if (this.people.find((p) => p._id === data._id)) {
          return;
        }
        if (this.sortByStatus !== 'all' && data.status !== this.sortByStatus) {
          return;
        }
        if (this.sortByVerify !== null && data.verified !== this.sortByVerify) {
          return;
        }
        this.people.unshift(data);
        this.totalPeople += 1;
      });

      socket.on('person.updated', (data) => {
        const index = this.people.findIndex((p) => p._id === data._id);
        if (index !== -1) {
          this.people.splice(index, 1, data);
        }
        // check if the person is selected, and update it
        if (this.selectedPeople && this.selectedPeople._id === data._id) {
          this.selectedPeople = data;
        }
      });

      socket.on('person.deleted', (data) => {
        this.people = this.people.filter((p) => p._id !== data._id);
        this.totalPeople -= 1;
      });

      socket.on('persons.bulk.imported', (data) => {
        this.totalPeople = data.count;
        this.getPeoples();
      });

      /**
       * Scraped Photo Events
       */
      socket.on('person.photo.added', (data) => {
        if (this.selectedPeople && data.peopleId === this.selectedPeople._id) {
          if (data.verified) {
            this.peoplePhotos.unshift(data);
          } else {
            this.peoplePhotos.push(data);
          }
        }
      });

      socket.on('person.photo.removed', (data) => {
        if (data.peopleId === this?.selectedPeople._id) {
          this.peoplePhotos = this.peoplePhotos.filter((p) => p._id !== data.photoId);
        }
      });

      // watch router changes to delete selectedPeople if ID is removed from URL
      router.afterEach((to) => {
        if (to.name === 'ulis') {
          if (!to.params.peopleId) {
            this.selectedPeople = null;
          }
        }
      });
    },

    // PEOPLE
    async getPeoples() {
      let url = `/peoples?page=${this.listPage}&limit=${this.listLimit}&search=${this.listSearch}&sortBy=${this.sortBy}&sortOrder=${this.sortOrder}`;

      if (this.sortByVerify !== null) {
        url = url.concat(`&verified=${this.sortByVerify.toString()}`);
      }
      if (this.selectedExtractionValue !== 'all' && this.selectedExtractionValue !== null) {
        url = url.concat(`&status=${this.selectedExtractionValue}`);
      }

      return await callAPI('GET', url, null)
        .then((res) => {
          this.people = res.result.map((people) => {
            // Format date based on available components
            const day = people.birthDateDay ? String(people.birthDateDay).padStart(2, '0') : null;
            const month = people.birthDateMonth ? String(people.birthDateMonth).padStart(2, '0') : null;
            const year = people.birthDateYear ? String(people.birthDateYear) : null;

            if (day && month && year) {
              people.birthDate = `${day}/${month}/${year}`;
            } else if (month && year) {
              people.birthDate = `${month}/${year}`;
            } else if (year) {
              people.birthDate = year;
            }
            return people;
          });
          this.totalPeople = res.total;
          this.getStats();
        })
        .catch((e) => {
          console.error(e);
        });
    },
    listItems(items) {
      return items.sort((a, b) => {
        return (b.verified === true) - (a.verified === true);
      });
    },
    async createPeople(people) {
      const requestObj = {
        status: 'Waiting',
        firstName: people.firstName?.trim(),
        lastName: people.lastName?.trim(),
        fullName: people.fullName?.trim(),
        nickName: people.nickName?.trim(),
        referenceAFP: people.referenceAFP?.trim(),
        wikidataId: people.wikidataId?.trim(),
        birthDateDay: people.birthDateDay?.trim(),
        birthDateMonth: people.birthDateMonth?.trim(),
        birthDateYear: people.birthDateYear?.trim(),
        job: people.job?.trim(),
        iptc: people.iptc?.trim(),
        // PATCH = remplacement complet : on envoie toujours ces deux champs.
        // Vide/null => le back retire le titre provisoire.
        provisionalTitle: people.provisionalTitle?.trim() || null,
        provisionalTitleEndDate: people.provisionalTitleEndDate || null,
      };
      if (people._id) {
        requestObj._id = people._id;
        return await callAPI('PATCH', '/peoples', requestObj).then((res) => {
          this.getPeoples();
          return res;
        });
      }
      return await callAPI('POST', '/peoples', requestObj).then((res) => {
        this.getPeoples();
        return res;
      });
    },
    async removePeople(_id) {
      return await callAPI('DELETE', `/peoples/${_id}`, null).then((res) => {
        this.getPeoples();
        this.people = res;
        this.selectedPeople = null;
        notify.success(i18n.global.t('add-people.person-deleted-title'), {
          description: i18n.global.t('add-people.person-deleted-description'),
        });
      });
    },

    _xhrPost(path, formData) {
      const userStore = useUserStore();
      const token = userStore.keycloak?.token;
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', serverUrls.authUrl + path, true);
        xhr.setRequestHeader('Authorization', 'Bearer ' + token);
        const makeError = () =>
          Object.assign(new Error(`XHR ${xhr.status}`), {
            status: xhr.status,
            statusText: xhr.statusText,
            responseText: xhr.responseText,
          });
        xhr.onreadystatechange = () => {
          if (xhr.readyState !== XMLHttpRequest.DONE) return;
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr);
          else reject(makeError());
        };
        xhr.onerror = () => reject(makeError());
        xhr.send(formData);
      });
    },

    handleUploadResponse(xhr) {
      const res = JSON.parse(xhr.responseText);

      for (let error of res.errors) {
        notify.error(i18n.global.t('add-people.file-error-title', { filename: error.filename }), {
          description: error.reason,
        });
      }
    },

    async createPeopleWithPhotos(peopleData, photoFiles) {
      const formData = new FormData();
      Object.entries(peopleData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });
      photoFiles.forEach((file) => formData.append('file', file));

      return this._xhrPost('/peoples', formData).then((xhr) => {
        this.handleUploadResponse(xhr);
        notify.success(i18n.global.t('add-people.person-created-title'), {
          description: i18n.global.t('add-people.person-created-description'),
        });
      });
    },

    async createPeoplesByCSV(files, { provisional = false, provisionalTitleEndDate = null } = {}) {
      const formData = new FormData();
      files.forEach((f) => formData.append('file', f));
      if (provisional) {
        formData.append('provisional', 'true');
        // Date nue YYYY-MM-DD telle que saisie : le back ne garde que le jour.
        formData.append('provisionalTitleEndDate', provisionalTitleEndDate);
      }

      return this._xhrPost('/peoples/upload', formData)
        .then((xhr) => {
          this.handleUploadResponse(xhr);
          notify.success(i18n.global.t('add-people.people-created-title'), {
            description: i18n.global.t('add-people.people-created-description'),
          });
          return xhr.responseURL;
        })
        .catch((error) => {
          this.csvEvent = null;
          let errorResponse;
          try {
            errorResponse = JSON.parse(error.responseText);
          } catch {
            errorResponse = { message: error.statusText || 'Erreur inconnue' };
          }
          // 400 = date de fin manquante / déjà passée → message explicite du back si dispo.
          const description =
            error.status === 400
              ? errorResponse.message || i18n.global.t('add-people.provisional-date-error')
              : i18n.global.t('add-people.error');
          notify.error(null, { description });
          throw Object.assign(new Error('Upload failed'), {
            status: error.status,
            statusText: error.statusText || 'Erreur réseau',
            response: errorResponse,
          });
        });
    },

    async getStats() {
      return await callAPI('GET', '/peoples/statistics', null)
        .then((res) => {
          this.stats = res;
        })
        .catch((e) => {
          console.error(e);
        });
    },

    //SCRAPPER
    isScrapCooldown(_id) {
      return this.scrapCooldownIds.includes(_id);
    },
    startScrapCooldown(_id) {
      if (!this.scrapCooldownIds.includes(_id)) {
        this.scrapCooldownIds.push(_id);
      }
      if (scrapCooldownTimers[_id]) clearTimeout(scrapCooldownTimers[_id]);
      scrapCooldownTimers[_id] = setTimeout(() => {
        this.scrapCooldownIds = this.scrapCooldownIds.filter((id) => id !== _id);
        delete scrapCooldownTimers[_id];
      }, 5000);
    },
    async scrapPeople(_id, fullName) {
      if (this.isScrapCooldown(_id)) return;
      this.startScrapCooldown(_id);
      return await callAPI('POST', `/scrap/${_id}`, null)
        .then(() => {
          notify.success(i18n.global.t('list-people.rescrap-people', { fullName: fullName || 'Person' }));
        })
        .catch(() => {});
    },
    async rescrapFailed() {
      return await callAPI('POST', '/scrap/retry-failed', null).then((res) => {
        // display notification to tell user it worked
        notify.success(i18n.global.t('list-people.rescrap-success'));
        return res;
      });
    },
    async syncDatabases() {
      return await callAPI('POST', '/ia/syncMilvus', { status: true }).then((res) => {
        return res;
      });
    },

    async getScrapedPhotos(peopleId) {
      this.peoplePhotos = [];
      return await callAPI('GET', `/scraped-photos/${peopleId}`, null)
        .then((res) => {
          this.peoplePhotos = this.listItems(res);
        })
        .catch((e) => {
          console.error(e);
        });
    },
    async blacklistFile(id) {
      return await callAPI('PATCH', `/scraped-photos/${id}/blacklist`, null).then(() => {
        this.getScrapedPhotos(this.selectedPeople._id);
      });
    },
    async verifiedFile(id) {
      return await callAPI('PATCH', `/scraped-photos/${id}/verified`, null).then(() => {
        this.getScrapedPhotos(this.selectedPeople._id);
      });
    },
    async addPhotoToPeople(file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('peopleId', this.selectedPeople._id);

      this.loadingPhotos = true;

      return this._xhrPost('/peoples/upload/manual', formData)
        .then((xhr) => {
          notify.success(i18n.global.t('people-photo.add-photo-title'), {
            description: i18n.global.t('people-photo.add-photo-description'),
          });
          return xhr.responseURL;
        })
        .catch((error) => {
          throw new Error(`Request failed with status: ${error.status}, response: ${error.responseText}`);
        })
        .finally(() => {
          this.loadingPhotos = false;
        });
    },

    async findOnePeople(_id) {
      return await callAPI('GET', `/peoples/${_id}`, null)
        .then((res) => {
          this.selectedPeople = res;
        })
        .catch((e) => {
          console.error(e);
        });
    },

    async getPageByPeopleId(peopleId) {
      if (!peopleId) {
        return;
      }

      let url = `?page=${this.listPage}&limit=${this.listLimit}&search=${this.listSearch}&sortBy=${this.sortBy}&sortOrder=${this.sortOrder}`;

      if (this.sortByVerify !== null) {
        url = url.concat(`&verified=${this.sortByVerify.toString()}`);
      }
      if (this.selectedExtractionValue !== 'all' && this.selectedExtractionValue !== null) {
        url = url.concat(`&status=${this.selectedExtractionValue}`);
      }

      return await callAPI('GET', `/peoples/page-by-people-id/${peopleId}${url}`, null)
        .then((res) => {
          if (!res.page) {
            router.push({
              name: 'ulis',
              params: { peopleId: null },
            });
            return;
          }

          this.people = res.result;
          this.totalPeople = res.total;
          this.listPage = res.page ?? this.listPage;
        })
        .catch((e) => {
          console.error(e);
        });
    },

    async verifyPeople() {
      return await callAPI('PATCH', `/peoples/${this.selectedPeople._id}/verify`, null)
        .then(() => {
          this.getPeoples();
        })
        .catch((e) => {
          console.error(e);
        });
    },

    async unverifyPeople() {
      return await callAPI('PATCH', `/peoples/${this.selectedPeople._id}/unverify`, null)
        .then(() => {
          this.getPeoples();
        })
        .catch((e) => {
          console.error(e);
        });
    },
  },
});
